import Item, {ItemAttributesSettable} from '~/shared/src/ci/Item';

import {DATA_UPDATE_ITEM} from '~/frontend/src/constants';
import {AppDispatch, StoreState, Thunk} from '~/frontend/src/store';
import {setElusiveData} from '~/frontend/src/actions/elusiveData';
import {MergeMode} from '~/frontend/src/utilities/dataUtils';


export type ItemUpdate = {
    itemId : number;
    attributes : ItemAttributesSettable;
    additionalElusiveDataContexts? : string[];
    mergeMode? : MergeMode;
};

/**
 * Bulk variant of updateItem. Processes N item updates and emits exactly one
 * setElusiveData per affected elusiveData context with all increments merged.
 */
const updateItems = ( dataContext : string, updates : ItemUpdate[] ) : Thunk<void> =>
    ( dispatch : AppDispatch, getState : () => StoreState ) => {
        if ( !updates.length ) {
            return;
        }

        const increments : { [ ctx : string ] : { [ itemId : number ] : number } } = {};

        for ( const { itemId, attributes, additionalElusiveDataContexts = [], mergeMode = 'override' } of updates ) {

            // notification action (kept for existing listeners e.g. TreeTest)
            dispatch( {
                type: DATA_UPDATE_ITEM,
                dataContext,
                itemId,
                attributes,
                additionalElusiveDataContexts
            } );

            const item : Item                                = global.data[ dataContext ]?.items[ itemId ],
                  attributesNames                            = Object.keys( attributes ),
                  changedAttributes : ItemAttributesSettable = {};

            if ( !item || !attributesNames.length ) {
                continue;
            }

            for ( const attributeName of attributesNames ) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                if ( item[ attributeName ] !== attributes[ attributeName ] ) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    changedAttributes[ attributeName ] = attributes[ attributeName ];
                }
            }

            if ( !Object.keys( changedAttributes ).length ) {
                continue;
            }

            item.clone( changedAttributes, mergeMode !== 'keepExisting' );

            for ( const ctx of [ dataContext, ...additionalElusiveDataContexts ] ) {
                ( increments[ ctx ] ??= {} );
                increments[ ctx ][ item.id ] = ( increments[ ctx ][ item.id ] ?? 0 ) + 1;
            }
        }

        // one setElusiveData per context, using current state as base
        for ( const ctx of Object.keys( increments ) as string[] ) {
            const elusiveData = getState().elusiveData?.[ ctx ],
                  items       = { ...elusiveData?.data?.items };

            for ( const idStr of Object.keys( increments[ ctx ] ) ) {
                const id = Number( idStr );
                items[ id ] = ( items[ id ] ?? 0 ) + increments[ ctx ][ id ];
            }

            dispatch( setElusiveData( ctx, {
                ...elusiveData,
                data: {
                    ...elusiveData?.data,
                    items,
                    references: elusiveData?.data?.references ?? {}
                }
            } ) );
        }
    };

export type UpdateItems = typeof updateItems;
export default updateItems;